import { Grid, Switch } from '@mui/material';
import { RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';

import { StopDTO } from '../../../../../../domain/stop/StopDTO';
import Button from '../../../../../../react-components/Button';
import Text from '../../../../../../react-components/Text';
import { CreateStopsList } from '../fields/CreateStopsList';
import { ReviewAddressesDataRow } from './ReviewAddressesDataRow';
import { AddressItem, AddressRow, ROW_HEIGHT } from './ReviewAddressesStyles';

/**
 * THIS IS A HACK FOR BROWSERS THAT HAS SCROLLBAR.
 * WE NEED THE WIDTH OF THE SCROLLBAR TO CALCULATE
 * THE WIDTH OF THE HEADER COLUMNS.
 * IT DOES NOT OCCUR IN FIREFOX
 * */
const getScrollBarWidth = () => {
  const el = document.createElement(`div`);
  el.style.cssText = `overflow:scroll; visibility:hidden; position:absolute;`;
  document.body.appendChild(el);
  const width = el.offsetWidth - el.clientWidth;
  el.remove();
  return width > 0 ? width + 1 : width;
};

interface ReviewAddressesProps {
  createStopsList: CreateStopsList;
  tasksEnabled: boolean;
  pickupsEnabled: boolean;
  onComplete: (createStopsList: CreateStopsList) => void;
  onBack: () => void;
}

export const ReviewAddresses = ({
  createStopsList,
  tasksEnabled,
  pickupsEnabled,
  onComplete,
  onBack,
}: ReviewAddressesProps) => {
  const [tempCreateStopsList, setTempCreateStopsList] = useState<CreateStopsList>(createStopsList);
  const [tab, setTab] = useState<`stops` | `pickups`>(`stops`);
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [showWarnings, setShowWarnings] = useState<boolean>(false);
  const [itemsListOverflow, setItemsListOverflow] = useState<boolean>(false);

  return (
    <Grid container direction="column">
      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{
          height: `calc(100% - 80px)`,
          border: `1px solid ${RoutalPalette.grey4}`,
          borderRadius: `10px`,
          padding: `20px`,
          overflow: `auto`,
        }}
      >
        <Grid container direction="row" alignItems="center" wrap="nowrap">
          <Grid item container direction="row" alignItems="center" style={{ width: `unset` }}>
            <Switch value={showErrors} onChange={() => setShowErrors(!showErrors)} />
            <Text intlId="plans.dialogs.upload_stops.review_addresses.filters.show_only_errors" />
          </Grid>

          <Grid item container direction="row" alignItems="center" style={{ width: `unset` }}>
            <Switch value={showWarnings} onChange={() => setShowWarnings(!showWarnings)} />
            <Text intlId="plans.dialogs.upload_stops.review_addresses.filters.show_only_warnings" />
          </Grid>

          {pickupsEnabled && tempCreateStopsList.hasPickups() && (
            <Grid item container direction="row" alignItems="center" style={{ width: `unset` }}>
              <Switch value={tab === `pickups`} onChange={() => setTab(tab === `pickups` ? `stops` : `pickups`)} />
              <Text intlId="plans.dialogs.upload_stops.review_addresses.filters.show_only_pickups" />
            </Grid>
          )}
        </Grid>

        <VSpacer medium />

        <Grid container direction="column" wrap="nowrap" style={{ position: `relative`, height: `100%` }}>
          <AddressRow
            container
            wrap="nowrap"
            style={{
              backgroundColor: RoutalPalette.grey10,
              textAlign: `center`,
              position: `sticky`,
            }}
          >
            <AddressItem
              container
              alignItems="center"
              justifyContent="center"
              style={{ width: `50px`, height: `${ROW_HEIGHT}px` }}
            ></AddressItem>
            {tasksEnabled && (
              <AddressItem
                container
                alignItems="center"
                justifyContent="center"
                style={{ width: `5%`, height: `${ROW_HEIGHT}px` }}
              >
                <Text intlId="plans.dialogs.upload_stops.review_addresses.tasks" isBold />
              </AddressItem>
            )}
            <AddressItem
              container
              alignItems="center"
              justifyContent="center"
              style={{ width: `20%`, height: `${ROW_HEIGHT}px` }}
            >
              <Text intlId="plans.dialogs.upload_stops.review_addresses.label" isBold />
            </AddressItem>
            <AddressItem
              container
              alignItems="center"
              justifyContent="center"
              style={{ width: `35%`, height: `${ROW_HEIGHT}px` }}
            >
              <Text intlId="plans.dialogs.upload_stops.review_addresses.original_address" isBold />
            </AddressItem>
            <AddressItem
              container
              alignItems="center"
              justifyContent="center"
              style={{
                width: `calc(${tasksEnabled ? `40%` : `45%`} + ${itemsListOverflow ? getScrollBarWidth() : 0}px`,
                height: `${ROW_HEIGHT}px`,
              }}
            >
              <Text intlId="plans.dialogs.upload_stops.review_addresses.geocoded_address" isBold />
            </AddressItem>
          </AddressRow>

          <Grid flexGrow={1}>
            <AutoSizer>
              {({ height, width }: { height: number; width: number }) => {
                const arePickups = tab === `pickups`;
                const data = arePickups ? createStopsList.getPickups() : createStopsList.getStops();

                const noUndefinedData = data.filter((stop: StopDTO | undefined) => stop !== undefined) as StopDTO[];

                const filteredData = noUndefinedData.reduce((acc, stop) => {
                  const location = arePickups
                    ? createStopsList.getPickupLocation(stop.id)
                    : createStopsList.getStopLocation(stop.id);

                  const hasErrors = !location?.isValid();
                  const hasWarnings = location?.partial_match;

                  if ((showErrors || showWarnings) && !hasErrors && !hasWarnings) {
                    return acc;
                  }
                  if (showErrors && !showWarnings && !hasErrors) {
                    return acc;
                  }
                  if (showWarnings && !showErrors && !hasWarnings) {
                    return acc;
                  }
                  return [...acc, stop];
                }, [] as StopDTO[]);

                setItemsListOverflow((height ?? 0) < filteredData.length * ROW_HEIGHT);

                return (
                  <List
                    height={height!}
                    itemCount={filteredData.length}
                    itemSize={ROW_HEIGHT}
                    width={width!}
                    itemData={{
                      tasksEnabled: tasksEnabled,
                      arePickups,
                      showErrors,
                      showWarnings,
                      createStopsList,
                      setCreateStopsList: setTempCreateStopsList,
                    }}
                  >
                    {ReviewAddressesDataRow}
                  </List>
                );
              }}
            </AutoSizer>
          </Grid>
        </Grid>
      </Grid>

      <Grid container direction="row" alignItems="center" justifyContent="space-between" style={{ height: `80px` }}>
        <Button
          variant="flat"
          styleType="secondary"
          style={{ minWidth: `80px` }}
          onClick={() => onBack()}
          intlId="back"
        />

        <Button
          intlId="plans.dialogs.upload_stops.create_stops"
          style={{ minWidth: `80px` }}
          onClick={() => {
            onComplete(tempCreateStopsList);
          }}
        />
      </Grid>
    </Grid>
  );
};
